import { AxiosError, AxiosResponse } from 'axios';
import {Observable, catchError, from, map, tap, switchMap} from 'rxjs';

import APIAxios, { APIRoutes } from '@api/axios.api';
import SnackError from '@utils/error.utils';

import { setEntities } from '@ngneat/elf-entities';
import {
    AuditOffer,
    AuditOfferCreationDto,
    AuditOfferGroupedDto, AuditOfferGroupedOfferSupplierDto,
    AuditOfferGroupedSupplierOfferDto,
    AuditOfferPriceFilterActionDto,
    AuditOfferPriceGroupedActionDto,
    AuditOfferServiceFilterActionsDto,
    AuditOfferServiceGroupedActionsDto,
    AuditOfferUpdateDto, ContractStore, OfferActionArray,
    OfferActionArrayLight,
    OfferFormalizationAction
} from './audit-offer.model';
import {getAuditOfferDataSource, getAuditOffersDataSource, getEngagedSupplierDataSource} from './audit-offer.requests';
import { auditOfferStore } from './audit-offer.store';
import {EngagedSupplierData} from '@screens/admin/audits/audit-detail/tabs/AuditsOfferActivation.tab';
import {extractFilenameFromResponse} from '@utils/response.utils';
import {filesService} from '@store/files';

export class AuditsOfferService {
  store = auditOfferStore;

  resetStore = () => this.store.reset();

  setVersion = (version: number) => this.store.update((state) => ({
    ...state,
    version,
  }));

    setAction = (action: Partial<OfferActionArray>) =>
        this.store.update((state) => ({
            ...state,
            actionArray: {
                ...state.actionArray,
                ...action,
            },
        }));

    setContract = (action: Partial<ContractStore>) =>
        this.store.update((state) => ({
            ...state,
            contract: {
                ...state.contract,
                ...action,
            },
        }));

    setActionFormalization = (action: Partial<OfferFormalizationAction>) =>
        this.store.update((state) => ({
            ...state,
            auditsFormalizationAction: {
                ...state.auditsFormalizationAction,
                ...action,
            },
        }));

  getAuditOffer = (auditId: number | string, version: number): Observable<AuditOffer[]> => {
    return from(
      APIAxios({
        ...APIRoutes.GETAuditsOffer(auditId, version),
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<AuditOffer[]>) => {
        return response.data;
      }),
      tap((auditOffers) => {
        this.setVersion(version);
        this.store.update(
          setEntities(auditOffers),
          getAuditOfferDataSource.setSuccess(),
        );
      }),
      getAuditOfferDataSource.trackRequestStatus(),
    );
  };

  createAuditOffer = (data: AuditOfferCreationDto) => {
    return from(APIAxios({
      ...APIRoutes.PATCHAuditsOffer(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<any>) => {
          return response.data;
        }),
      );
  };

  deleteAuditOffer = (auditOfferId: number | string) => {
    return from(APIAxios(APIRoutes.DELETEAuditsOffer(auditOfferId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  getAuditOfferCSV = (auditId: number | string) => {
    return from(
        APIAxios({
          ...APIRoutes.GETAuditsOfferCSV(auditId)
        })
    ).pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap((response: AxiosResponse<Blob>) => {
          const filename = extractFilenameFromResponse(response);
          filesService.downloadDocument(response.data, filename || 'export.xlsx');
        }),
    );
  };

    getAuditOfferByAuditCSV = (auditId: number | string) => {
        return from(
            APIAxios({
                ...APIRoutes.GETAuditsOfferByAuditCSV(auditId)
            })
        ).pipe(
            catchError((err: AxiosError) => {
                throw new SnackError((err.response as any)?.data?.message, 'error');
            }),
            tap((response: AxiosResponse<Blob>) => {
                const filename = extractFilenameFromResponse(response);
                filesService.downloadDocument(response.data, filename || 'export.xlsx');
            }),
        );
    };

  PatchPriceGroupActions = (data : AuditOfferPriceGroupedActionDto) => {
    return from(APIAxios({
      ...APIRoutes.PATCHPriceGroupedActions(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<any>) => {
          return response.data;
        }),
      );
  };

  PatchPriceFilterActions = (auditId : number | string, data: AuditOfferPriceFilterActionDto) => {
    return from(APIAxios({
      ...APIRoutes.PATCHPriceFilterActions(auditId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<any>) => {
          return response.data;
        }),
      );
  };

  GETAuditOffersByAudit = (auditId: number | string) => {
    return from(APIAxios({
      ...APIRoutes.GETAuditOffersByAudit(auditId),
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<AuditOfferGroupedDto[]>) => {
          return response.data;
        }),
        tap((auditOffers) => {
          this.store.update((state) => ({
            ...state,
            auditOffers,
        }));
        getAuditOffersDataSource.setSuccess();
        }),
        getAuditOffersDataSource.trackRequestStatus(),
      );
    };

  GETAuditsOffersSuppliersByAuditOfferId = (auditOfferId: number | string) => {
      return from(APIAxios({
        ...APIRoutes.GETAuditsOffersSuppliersByAuditOfferId(auditOfferId),
      }))
        .pipe(
          catchError((err: AxiosError) => {
            throw new SnackError((err.response as any)?.data?.message, 'error');
          }),
          map((response: AxiosResponse<any>) => {
            return response.data;
          }),
        );
  };

    GETGroupedAuditsOffersSuppliers = (data: AuditOfferGroupedOfferSupplierDto) => {
        return from(APIAxios({
            ...APIRoutes.GETGroupedAuditsOffersSuppliers(),
            data
        }))
            .pipe(
                catchError((err: AxiosError) => {
                    throw new SnackError((err.response as any)?.data?.message, 'error');
                }),
                map((response: AxiosResponse<any>) => {
                    return response.data;
                }),
            );
    };

    ActivateOfferByAuditId = (auditId: number | string) => {
        return from(APIAxios({
            ...APIRoutes.POSTActivateOffersByAuditId(auditId),
        }))
            .pipe(
                catchError((err: AxiosError) => {
                    throw new SnackError((err.response as any)?.data?.message, 'error');
                }),
                map((response: AxiosResponse<any>) => {
                    return response.data;
                }),
                switchMap(() => this.GETAuditOffersByAudit(auditId))
            );
    };

    PutGroupedAuditsOffersSuppliers = (data: AuditOfferGroupedSupplierOfferDto) => {
        return from(APIAxios({
            ...APIRoutes.PatchGroupedAuditsOffersSuppliers(),
            data
        }))
            .pipe(
                catchError((err: AxiosError) => {
                    throw new SnackError((err.response as any)?.data?.message, 'error');
                }),
                map((response: AxiosResponse<any>) => {
                    return response.data;
                }),
            );
    };

  GETSelectedSuppliersByAuditId = (auditId: number | string) => {
        return from(APIAxios({
          ...APIRoutes.GETSelectedSuppliers(auditId),
        }))
          .pipe(
            catchError((err: AxiosError) => {
              throw new SnackError((err.response as any)?.data?.message, 'error');
            }),
            map((response: AxiosResponse<EngagedSupplierData[]>) => {
              return response.data;
            }),
              tap((engagedSuppliers) => {
                  this.store.update((state) => ({
                      ...state,
                      engagedSuppliers,
                  }));
                  getEngagedSupplierDataSource.setSuccess();
              }),
              getEngagedSupplierDataSource.trackRequestStatus(),
          );
  };

  PatchAuditOfferById = (auditOfferId: number | string, data: AuditOfferUpdateDto) => {
        return from(APIAxios({
          ...APIRoutes.PATCHAuditOffer(auditOfferId),
          data,
        }))
          .pipe(
            catchError((err: AxiosError) => {
              throw new SnackError((err.response as any)?.data?.message, 'error');
            }),
            map((response: AxiosResponse<any>) => {
              return response.data;
            }),
          );
  };

  PATCHServiceGroupedActions = (data : AuditOfferServiceGroupedActionsDto) => {
    return from(APIAxios({
      ...APIRoutes.PATCHServiceGroupedActions(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<any>) => {
          return response.data;
        }),
      );
  };

    PATCHPriceGroupedActions = (data : OfferFormalizationAction) => {
    return from(APIAxios({
      ...APIRoutes.PATCHPriceGroupedActions(),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<any>) => {
          return response.data;
        }),
      );
  };

  PATCHUpdateValidate = (auditOperationId: string| number,  data: OfferActionArrayLight) => {
    return from(APIAxios({
      ...APIRoutes.PATCHUpdateValidate(auditOperationId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<any>) => {
          return response.data;
        }),
      );
  };

  PATCHServiceFilterActions = (auditId : number | string, data: AuditOfferServiceFilterActionsDto) => {
    return from(APIAxios({
      ...APIRoutes.PATCHServiceFilterActions(auditId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<any>) => {
          return response.data;
        }),
      );
  };
}

export const auditsOfferService = new AuditsOfferService();
